import { useQuery } from "@apollo/client";
import { BaseTable, IBaseTableProps } from "@sede-x/shell-ds-react-framework";
import { getPaginationRowModel } from "@tanstack/react-table";
import dayjs from "dayjs";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import LoadingPanel from "shared/components/LoadingPanel";
import { GqlResponse, TVolumeAllocationsOfMovementAtRevision } from "ticketing/ticketing.types";

type Response = GqlResponse<
  TVolumeAllocationsOfMovementAtRevision[],
  "volumeAllocationsOfMovementAtRevision"
>;

const GET_AUDIT_ALLOCATIONS = loader(
  "../../ticketing-graphql/volumeAllocationsOfMovementAtRevision.graphql"
);

/**
 * LastModifiedByCell
 * @param props
 * @returns
 */

type LastModifiedByCellProps = {
  row: {
    original: TVolumeAllocationsOfMovementAtRevision;
  };
};
const LastModifiedByCell = ({ row }: LastModifiedByCellProps) => {
  const item = row.original;
  const displayName = [item.lastModifiedBy.lastName, item.lastModifiedBy.firstName]
    .filter(Boolean)
    .join(", ");
  return <span title={item.lastModifiedBy.id}>{displayName}</span>;
};

const transformData = (volumeAllocations: TVolumeAllocationsOfMovementAtRevision[]) => {
  return volumeAllocations
    ?.map(v => ({
      ...v,
      groupId: v.groupId.split("-")[0],
      lastModifiedDate: new Date(v.lastModifiedDate + "Z"),
      targetEnterpriseSystemCode:
        v.srcEnterpriseSystemCode === v.targetEnterpriseSystemCode
          ? ""
          : v.targetEnterpriseSystemCode,
      volume: v.volume === 0 ? "" : v.volume
    }))
    .sort((a, b) => b.lastModifiedDate.getTime() - a.lastModifiedDate.getTime());
};

type RevisionsMovementAllocationsProps = {
  movementId: string;
  revisionNumber: string;
};

const columns: IBaseTableProps<TVolumeAllocationsOfMovementAtRevision>["columns"] = [
  {
    header: "Updated By",
    accessorKey: "lastModifiedBy",
    size: 140,
    cell: LastModifiedByCell
  },
  {
    header: "Update At",
    accessorKey: "lastModifiedDate",
    size: 200,
    cell: ({ row }) => dayjs(row.original.lastModifiedDate).format("MM/DD/YY HH:mm:ss")
  },
  {
    header: "Source Delivery ID",
    accessorKey: "srcEnterpriseSystemCode",
    size: 200
  },
  {
    header: "Target Delivery ID",
    accessorKey: "targetEnterpriseSystemCode",
    size: 200
  },
  {
    header: "Allocation Quantity",
    accessorKey: "volume",
    size: 140
  },
  {
    header: "Reason",
    accessorKey: "reason",
    size: 140
  },
  {
    header: "Group ID",
    accessorKey: "groupId",
    size: 140
  },
  {
    header: "Allocation ID",
    accessorKey: "id",
    size: 140
  }
];

/**
 * RevisionsMovementAllocations
 * @param param0
 * @returns
 */
const RevisionsMovementAllocations = ({
  movementId,
  revisionNumber
}: RevisionsMovementAllocationsProps) => {
  const { loading, error } = useQuery<Response>(GET_AUDIT_ALLOCATIONS, {
    fetchPolicy: "no-cache",
    variables: { movementId, revisionNumber },
    onCompleted: data =>
      setVolumeAllocations(transformData(data.volumeAllocationsOfMovementAtRevision))
  });

  const [volumeAllocations, setVolumeAllocations] =
    useState<TVolumeAllocationsOfMovementAtRevision[]>();

  return (
    <>
      {loading && <LoadingPanel />}
      {error && <ApolloErrorViewer error={error} />}

      <BaseTable
        data={volumeAllocations || []}
        columns={columns}
        tableOptions={{
          defaultColumn: {
            minSize: 100,
            maxSize: 400
          },
          enableColumnResizing: true,
          columnResizeMode: "onChange",
          getPaginationRowModel: getPaginationRowModel()
        }}
        style={{ width: "100%", tableLayout: "fixed" }}
      />
    </>
  );
};

export default RevisionsMovementAllocations;
